// src/pages/Placements.jsx

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { Link } from "react-router-dom";
import {
  HiArrowRight,
  HiCheckBadge,
  HiMagnifyingGlass,
} from "react-icons/hi2";

import PlacementLogoGrid from "../components/PlacementLogoGrid";
import PlacementGrid from "../components/PlacementGrid";
import StudentVideoTestimonialsSection from "../components/StudentVideoTestimonialsSection";
import StudentTestimonialsSection from "../components/StudentTestimonialsSection";
import placedStudents from "../data/placedStudents";

/* =========================================================
   PREVIOUS PLACEMENT STATS
   KEEP AS PROVIDED
========================================================= */

const stats = [
  {
    label: "Students Placed",
    value: 1000,
    suffix: "+",
    note: "Career outcomes",
  },
  {
    label: "Hiring Companies",
    value: 50,
    suffix: "+",
    note: "Across placement records",
  },
  {
    label: "Highest Package",
    value: 25,
    suffix: " LPA",
    note: "Recorded outcome",
  },
];

/* =========================================================
   HELPERS
========================================================= */

const getPackageValue = (value) => {
  const number = parseFloat(
    String(value || "").replace(/[^\d.]/g, "")
  );

  return Number.isFinite(number) ? number : 0;
};

/* =========================================================
   PAGE
========================================================= */

const Placements = () => {
  /* =======================================================
     PUBLISHED STUDENTS
  ======================================================== */

  const publishedStudents = useMemo(
    () =>
      placedStudents
        .filter((student) => student.published)
        .sort(
          (a, b) =>
            getPackageValue(b.package) -
            getPackageValue(a.package)
        ),
    []
  );

  /* =======================================================
     COURSE FILTER DATA
  ======================================================== */

  const courses = useMemo(() => {
    return Array.from(
      new Set(
        publishedStudents
          .map((student) => student.course)
          .filter(Boolean)
      )
    );
  }, [publishedStudents]);

  /* =======================================================
     FILTER STATE
  ======================================================== */

  const [search, setSearch] = useState("");
  const [activeCourse, setActiveCourse] = useState("All");

  /* =======================================================
     FILTERED STUDENTS
  ======================================================== */

  const filteredStudents = useMemo(() => {
    const query = search.trim().toLowerCase();

    return publishedStudents.filter((student) => {
      const courseMatch =
        activeCourse === "All" ||
        student.course === activeCourse;

      const searchMatch =
        !query ||
        student.name?.toLowerCase().includes(query) ||
        student.company?.toLowerCase().includes(query) ||
        student.course?.toLowerCase().includes(query) ||
        student.role?.toLowerCase().includes(query);

      return courseMatch && searchMatch;
    });
  }, [
    publishedStudents,
    search,
    activeCourse,
  ]);

  /* =======================================================
     FEATURED STUDENT
  ======================================================== */

  const featuredStudent =
    publishedStudents.find(
      (student) => student.featured
    ) || publishedStudents[0];

  /* =======================================================
     SEO
  ======================================================== */

  useEffect(() => {
    const title =
      "Student Placements & Career Outcomes | IA4Solutions";

    const description =
      "Explore IA4Solutions student placement stories, hiring companies, career outcomes and salary packages from our training community.";

    document.title = title;

    let descriptionMeta = document.querySelector(
      'meta[name="description"]'
    );

    if (!descriptionMeta) {
      descriptionMeta = document.createElement("meta");
      descriptionMeta.setAttribute(
        "name",
        "description"
      );
      document.head.appendChild(descriptionMeta);
    }

    descriptionMeta.setAttribute(
      "content",
      description
    );

    let canonical = document.querySelector(
      'link[rel="canonical"]'
    );

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute(
      "href",
      `${window.location.origin}/placements`
    );
  }, []);

  /* =======================================================
     EMPTY STATE
  ======================================================== */

  if (!publishedStudents.length) {
    return null;
  }

  return (
    <main
      className="
        min-h-screen
        overflow-hidden
        bg-[#fafbff]
        text-gray-950
        dark:bg-gray-950
        dark:text-white
      "
    >
      {/* =====================================================
          AMBIENT BACKGROUND
      ====================================================== */}

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          left-1/4
          top-[-180px]
          h-[420px]
          w-[650px]
          rounded-full
          bg-indigo-500/[0.055]
          blur-[110px]
          dark:bg-indigo-500/[0.07]
        "
      />

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          right-[-120px]
          top-[140px]
          h-[280px]
          w-[280px]
          rounded-full
          bg-cyan-400/[0.035]
          blur-[100px]
          dark:bg-cyan-400/[0.04]
        "
      />

      {/* =====================================================
          PREMIUM HERO
      ====================================================== */}

      <section
        className="
          relative
          overflow-hidden
          px-4
          pb-10
          pt-20
          sm:px-6
          sm:pb-12
          sm:pt-24
          lg:px-8
          lg:pb-14
          lg:pt-28
        "
      >
        <div className="relative mx-auto max-w-7xl">

          {/* Breadcrumb */}

          <div
            className="
              mb-6
              flex
              items-center
              gap-2
              text-[9px]
              font-medium
              text-gray-400
            "
          >
            <Link
              to="/"
              className="
                transition-colors
                hover:text-indigo-600
                dark:hover:text-indigo-400
              "
            >
              Home
            </Link>

            <span>/</span>

            <span
              className="
                font-semibold
                text-gray-500
                dark:text-gray-400
              "
            >
              Placements
            </span>
          </div>

          {/* Hero grid */}

          <div
            className="
              grid
              items-center
              gap-8
              lg:grid-cols-[1.02fr_0.98fr]
              lg:gap-12
              xl:gap-16
            "
          >

            {/* =================================================
                HERO COPY
            ================================================== */}

            <motion.div
              initial={{
                opacity: 0,
                y: 16,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.55,
                ease: "easeOut",
              }}
              className="max-w-2xl"
            >
              {/* Eyebrow */}

              <div
                className="
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  border
                  border-indigo-100
                  bg-white/80
                  px-3
                  py-1.5
                  text-[8px]
                  font-bold
                  uppercase
                  tracking-[0.17em]
                  text-indigo-600
                  shadow-sm
                  backdrop-blur-sm
                  dark:border-indigo-900/50
                  dark:bg-gray-900/70
                  dark:text-indigo-400
                "
              >
                <span
                  className="
                    h-1.5
                    w-1.5
                    rounded-full
                    bg-indigo-500
                    shadow-[0_0_0_4px_rgba(99,102,241,0.10)]
                  "
                />

                Student Placement Stories
              </div>

              {/* Main heading */}

              <h1
                className="
                  mt-5
                  max-w-2xl
                  text-[2.45rem]
                  font-extrabold
                  leading-[0.98]
                  tracking-[-0.06em]
                  text-gray-950
                  sm:text-5xl
                  lg:text-[4rem]
                  dark:text-white
                "
              >
                From learning
                <br />

                <span
                  className="
                    bg-gradient-to-r
                    from-indigo-600
                    via-blue-600
                    to-cyan-500
                    bg-clip-text
                    text-transparent
                  "
                >
                  to career.
                </span>
              </h1>

              {/* Description */}

              <p
                className="
                  mt-5
                  max-w-xl
                  text-sm
                  leading-7
                  text-gray-600
                  sm:text-[15px]
                  dark:text-gray-300
                "
              >
                Explore real placement journeys from
                IA4Solutions students and discover the
                companies, roles and career outcomes built
                through practical training.
              </p>

              {/* Actions */}

              <div
                className="
                  mt-7
                  flex
                  flex-wrap
                  items-center
                  gap-3
                "
              >
                <a
                  href="#student-outcomes"
                  className="
                    group
                    inline-flex
                    items-center
                    gap-2
                    rounded-xl
                    bg-gray-950
                    px-5
                    py-3
                    text-[11px]
                    font-bold
                    text-white
                    shadow-[0_12px_30px_rgba(15,23,42,0.14)]
                    transition-all
                    duration-200
                    hover:-translate-y-0.5
                    hover:bg-indigo-600
                    dark:bg-white
                    dark:text-gray-950
                    dark:hover:bg-indigo-500
                    dark:hover:text-white
                  "
                >
                  Explore placement stories

                  <HiArrowRight
                    className="
                      transition-transform
                      duration-200
                      group-hover:translate-x-0.5
                    "
                  />
                </a>

                <Link
                  to="/register"
                  className="
                    inline-flex
                    items-center
                    rounded-xl
                    border
                    border-gray-200
                    bg-white/80
                    px-5
                    py-3
                    text-[11px]
                    font-bold
                    text-gray-700
                    shadow-sm
                    backdrop-blur-sm
                    transition-all
                    duration-200
                    hover:-translate-y-0.5
                    hover:border-indigo-200
                    hover:text-indigo-600
                    dark:border-gray-800
                    dark:bg-gray-900/80
                    dark:text-gray-200
                    dark:hover:border-indigo-800
                    dark:hover:text-indigo-400
                  "
                >
                  Start your journey
                </Link>
              </div>

              {/* Trust line */}

              <div
                className="
                  mt-7
                  flex
                  flex-wrap
                  items-center
                  gap-x-5
                  gap-y-2
                  text-[9px]
                  font-medium
                  text-gray-400
                "
              >
                <span className="inline-flex items-center gap-1.5">
                  <HiCheckBadge className="text-xs text-emerald-500" />
                  Real student outcomes
                </span>

                <span className="h-3 w-px bg-gray-200 dark:bg-gray-800" />

                <span>
                  Technology · Analytics · Digital Marketing
                </span>
              </div>
            </motion.div>

            {/* =================================================
                FEATURED OUTCOME
            ================================================== */}

            <motion.div
              initial={{
                opacity: 0,
                y: 18,
                scale: 0.985,
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
              }}
              transition={{
                duration: 0.6,
                delay: 0.08,
                ease: "easeOut",
              }}
              className="relative"
            >
              {/* Outer glow */}

              <div
                aria-hidden="true"
                className="
                  absolute
                  -inset-4
                  rounded-[34px]
                  bg-gradient-to-br
                  from-indigo-500/[0.10]
                  via-blue-500/[0.04]
                  to-cyan-400/[0.08]
                  blur-2xl
                "
              />

              {/* Panel */}

              <div
                className="
                  relative
                  overflow-hidden
                  rounded-[26px]
                  border
                  border-gray-200
                  bg-gray-950
                  shadow-[0_24px_70px_rgba(15,23,42,0.13)]
                  dark:border-gray-800
                  dark:shadow-[0_24px_70px_rgba(0,0,0,0.28)]
                "
              >
                {/* Accent */}

                <div
                  className="
                    h-[3px]
                    bg-gradient-to-r
                    from-indigo-500
                    via-blue-500
                    to-cyan-400
                  "
                />

                {/* Image stage */}

                <div
                  className="
                    relative
                    h-[330px]
                    overflow-hidden
                    sm:h-[370px]
                    lg:h-[385px]
                  "
                >
                  {/* Background image */}

                  {featuredStudent.image && (
                    <img
                      src={featuredStudent.image}
                      alt=""
                      aria-hidden="true"
                      className="
                        absolute
                        inset-0
                        h-full
                        w-full
                        scale-110
                        object-cover
                        opacity-20
                        blur-2xl
                      "
                    />
                  )}

                  {/* Dark glass */}

                  <div
                    aria-hidden="true"
                    className="
                      absolute
                      inset-0
                      bg-gradient-to-b
                      from-gray-950/25
                      via-gray-950/20
                      to-gray-950/90
                    "
                  />

                  {/* Complete student image */}

                  {featuredStudent.image && (
                    <div
                      className="
                        absolute
                        inset-4
                        flex
                        items-center
                        justify-center
                        overflow-hidden
                        rounded-[20px]
                        border
                        border-white/10
                        sm:inset-5
                      "
                    >
                      <img
                        src={featuredStudent.image}
                        alt={featuredStudent.name}
                        className="
                          relative
                          z-10
                          h-full
                          w-full
                          object-contain
                          p-4
                          drop-shadow-[0_20px_32px_rgba(0,0,0,0.36)]
                        "
                      />
                    </div>
                  )}

                  {/* Featured badge */}

                  <div
                    className="
                      absolute
                      left-5
                      top-5
                      z-20
                      inline-flex
                      items-center
                      gap-1.5
                      rounded-full
                      border
                      border-white/10
                      bg-white/10
                      px-3
                      py-1.5
                      text-[8px]
                      font-bold
                      uppercase
                      tracking-[0.14em]
                      text-white
                      backdrop-blur-xl
                    "
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    Featured Placement
                  </div>

                  {/* Package badge */}

                  <div
                    className="
                      absolute
                      right-5
                      top-5
                      z-20
                      rounded-xl
                      border
                      border-white/10
                      bg-white/10
                      px-3
                      py-2
                      text-right
                      backdrop-blur-xl
                    "
                  >
                    <p
                      className="
                        text-[7px]
                        font-bold
                        uppercase
                        tracking-[0.12em]
                        text-white/45
                      "
                    >
                      Package
                    </p>

                    <p
                      className="
                        mt-0.5
                        text-lg
                        font-extrabold
                        tracking-[-0.03em]
                        text-white
                      "
                    >
                      {featuredStudent.package}
                    </p>
                  </div>

                  {/* Bottom student information */}

                  <div
                    className="
                      absolute
                      inset-x-0
                      bottom-0
                      z-20
                      p-5
                      sm:p-6
                    "
                  >
                    <p
                      className="
                        text-[8px]
                        font-bold
                        uppercase
                        tracking-[0.15em]
                        text-cyan-300
                      "
                    >
                      {featuredStudent.course}
                    </p>

                    <h2
                      className="
                        mt-1
                        text-2xl
                        font-extrabold
                        tracking-[-0.035em]
                        text-white
                        sm:text-[1.75rem]
                      "
                    >
                      {featuredStudent.name}
                    </h2>

                    <div className="mt-3 flex items-center gap-2.5">
                      <div
                        className="
                          flex
                          h-9
                          w-9
                          shrink-0
                          items-center
                          justify-center
                          rounded-xl
                          bg-white
                          p-1.5
                        "
                      >
                        {featuredStudent.companyLogo ? (
                          <img
                            src={featuredStudent.companyLogo}
                            alt={featuredStudent.company}
                            className="
                              h-full
                              w-full
                              object-contain
                            "
                          />
                        ) : (
                          <span
                            className="
                              text-[8px]
                              font-extrabold
                              text-gray-700
                            "
                          >
                            {featuredStudent.company?.slice(0, 2)}
                          </span>
                        )}
                      </div>

                      <div className="min-w-0">
                        <p
                          className="
                            text-[7px]
                            font-bold
                            uppercase
                            tracking-[0.12em]
                            text-white/40
                          "
                        >
                          Placed at
                        </p>

                        <p
                          className="
                            truncate
                            text-xs
                            font-bold
                            text-white
                          "
                        >
                          {featuredStudent.company}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom information strip */}

                <div
                  className="
                    grid
                    grid-cols-2
                    border-t
                    border-white/10
                    bg-white/[0.035]
                  "
                >
                  <div
                    className="
                      border-r
                      border-white/10
                      px-4
                      py-3.5
                    "
                  >
                    <p
                      className="
                        text-[7px]
                        font-bold
                        uppercase
                        tracking-[0.13em]
                        text-white/35
                      "
                    >
                      Training
                    </p>

                    <p
                      className="
                        mt-1
                        truncate
                        text-[9px]
                        font-semibold
                        text-white/80
                      "
                    >
                      {featuredStudent.course}
                    </p>
                  </div>

                  <div className="px-4 py-3.5">
                    <p
                      className="
                        text-[7px]
                        font-bold
                        uppercase
                        tracking-[0.13em]
                        text-white/35
                      "
                    >
                      Outcome
                    </p>

                    <p
                      className="
                        mt-1
                        flex
                        items-center
                        gap-1
                        text-[9px]
                        font-semibold
                        text-emerald-300
                      "
                    >
                      <HiCheckBadge className="text-xs" />
                      Career Placement
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* =====================================================
    PLACEMENT STATISTICS
===================================================== */}

      <section
        aria-label="Placement statistics"
        className="
    relative
    px-4
    pb-10
    sm:px-6
    sm:pb-12
    lg:px-8
    dark:bg-transparent
  "
      >
        <div className="mx-auto max-w-7xl">
          <div
            className="
        relative
        overflow-hidden
        rounded-[24px]
        border
        border-gray-200/80
        bg-white
        shadow-[0_12px_40px_rgba(15,23,42,0.06)]
        dark:border-gray-800
        dark:bg-gray-900/90
        dark:shadow-[0_16px_45px_rgba(0,0,0,0.24)]
      "
          >
            {/* Subtle ambient accents */}
            <div
              aria-hidden="true"
              className="
          pointer-events-none
          absolute
          -right-20
          -top-20
          h-40
          w-40
          rounded-full
          bg-indigo-500/[0.06]
          blur-3xl
          dark:bg-indigo-500/[0.10]
        "
            />

            <div
              aria-hidden="true"
              className="
          pointer-events-none
          absolute
          -bottom-24
          left-1/4
          h-44
          w-44
          rounded-full
          bg-cyan-400/[0.05]
          blur-3xl
          dark:bg-cyan-400/[0.08]
        "
            />

            <div
              className="
          relative
          grid
          grid-cols-1
          divide-y
          divide-gray-100
          sm:grid-cols-3
          sm:divide-x
          sm:divide-y-0
          dark:divide-gray-800
        "
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{
                    opacity: 0,
                    y: 10,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                    amount: 0.2,
                  }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.07,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="
              group
              relative
              px-5
              py-5
              sm:px-6
              sm:py-6
              lg:px-7
              lg:py-7
            "
                >
                  {/* Top accent */}
                  <div
                    aria-hidden="true"
                    className="
                absolute
                left-5
                right-5
                top-0
                h-[2px]
                origin-left
                scale-x-0
                rounded-full
                bg-gradient-to-r
                from-indigo-500
                via-blue-500
                to-cyan-400
                transition-transform
                duration-500
                group-hover:scale-x-100
                sm:left-6
                sm:right-6
                lg:left-7
                lg:right-7
              "
                  />

                  <div
                    className="
                flex
                items-center
                justify-between
                gap-4
              "
                  >
                    {/* Left content */}
                    <div className="min-w-0">
                      <div
                        className="
                    flex
                    items-center
                    gap-2
                  "
                      >
                        <span
                          aria-hidden="true"
                          className="
                      h-1.5
                      w-1.5
                      shrink-0
                      rounded-full
                      bg-indigo-500
                      shadow-[0_0_0_4px_rgba(99,102,241,0.08)]
                      dark:bg-indigo-400
                      dark:shadow-[0_0_0_4px_rgba(129,140,248,0.10)]
                    "
                        />

                        <p
                          className="
                      truncate
                      text-[8px]
                      font-bold
                      uppercase
                      tracking-[0.15em]
                      text-gray-400
                      sm:text-[9px]
                    "
                        >
                          {stat.label}
                        </p>
                      </div>

                      <div
                        className="
                    mt-1.5
                    flex
                    items-baseline
                    gap-1
                  "
                      >
                        <p
                          className="
                      text-[2rem]
                      font-black
                      leading-none
                      tracking-[-0.055em]
                      text-gray-950
                      sm:text-[2.15rem]
                      lg:text-[2.35rem]
                      dark:text-white
                    "
                        >
                          <CountUp
                            end={stat.value}
                            duration={1.5}
                            enableScrollSpy
                            scrollSpyOnce
                          />
                        </p>

                        <span
                          className="
                      text-sm
                      font-extrabold
                      tracking-[-0.02em]
                      text-indigo-600
                      sm:text-base
                      dark:text-indigo-400
                    "
                        >
                          {stat.suffix}
                        </span>
                      </div>

                      <p
                        className="
                    mt-1.5
                    text-[9px]
                    leading-5
                    text-gray-500
                    sm:text-[10px]
                    dark:text-gray-400
                  "
                      >
                        {stat.note}
                      </p>
                    </div>

                    {/* Right visual indicator */}
                    <div
                      className="
                  flex
                  h-10
                  w-10
                  shrink-0
                  items-center
                  justify-center
                  rounded-xl
                  border
                  border-gray-200
                  bg-gray-50
                  text-indigo-500
                  transition-all
                  duration-300
                  group-hover:-translate-y-0.5
                  group-hover:border-indigo-200
                  group-hover:bg-indigo-50
                  group-hover:text-indigo-600
                  sm:h-11
                  sm:w-11
                  dark:border-gray-700
                  dark:bg-gray-800/80
                  dark:text-indigo-400
                  dark:group-hover:border-indigo-800
                  dark:group-hover:bg-indigo-950/50
                  dark:group-hover:text-indigo-300
                "
                    >
                      <span
                        className="
                    h-2
                    w-2
                    rounded-full
                    bg-current
                    shadow-[0_0_12px_currentColor]
                  "
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Bottom micro strip */}
            <div
              className="
          relative
          border-t
          border-gray-100
          bg-gray-50/60
          px-5
          py-2.5
          text-center
          dark:border-gray-800
          dark:bg-gray-950/40
        "
            >
              <p
                className="
            text-[8px]
            font-medium
            tracking-[0.08em]
            text-gray-400
            sm:text-[9px]
          "
              >
                CAREER OUTCOMES • HIRING NETWORK • RECORDED PLACEMENT DATA
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          STUDENT OUTCOMES
      ====================================================== */}

      <section
        id="student-outcomes"
        className="
          scroll-mt-24
          border-y
          border-gray-100
          bg-white
          px-4
          py-12
          sm:px-6
          sm:py-14
          lg:px-8
          dark:border-gray-800
          dark:bg-gray-900
        "
      >
        <div className="mx-auto max-w-7xl">

          {/* Section heading */}

          <div
            className="
              flex
              flex-col
              gap-5
              sm:flex-row
              sm:items-end
              sm:justify-between
            "
          >
            <div>
              <p
                className="
                  text-[9px]
                  font-bold
                  uppercase
                  tracking-[0.15em]
                  text-indigo-600
                  dark:text-indigo-400
                "
              >
                Placement Stories
              </p>

              <h2
                className="
                  mt-1.5
                  text-2xl
                  font-extrabold
                  tracking-[-0.04em]
                  text-gray-950
                  sm:text-3xl
                  dark:text-white
                "
              >
                Our students. Their outcomes.
              </h2>

              <p
                className="
                  mt-2
                  max-w-xl
                  text-xs
                  leading-6
                  text-gray-500
                  sm:text-sm
                  dark:text-gray-400
                "
              >
                Explore current placement records by student,
                company and training program.
              </p>
            </div>

            <div
              className="
                shrink-0
                text-[10px]
                font-semibold
                text-gray-400
              "
            >
              {filteredStudents.length} of{" "}
              {publishedStudents.length} records
            </div>
          </div>

          {/* =================================================
              FILTER BAR
          ================================================== */}

          <div
            className="
              mt-7
              rounded-2xl
              border
              border-gray-200
              bg-gray-50/70
              p-3
              dark:border-gray-800
              dark:bg-gray-950/50
            "
          >
            {/* Search */}

            <div className="relative">
              <HiMagnifyingGlass
                className="
                  pointer-events-none
                  absolute
                  left-3.5
                  top-1/2
                  -translate-y-1/2
                  text-sm
                  text-gray-400
                "
              />

              <input
                type="search"
                value={search}
                onChange={(event) =>
                  setSearch(event.target.value)
                }
                placeholder="Search student, company or course..."
                className="
                  h-10
                  w-full
                  rounded-xl
                  border
                  border-gray-200
                  bg-white
                  pl-9
                  pr-3
                  text-[11px]
                  font-medium
                  text-gray-800
                  outline-none
                  placeholder:text-gray-400
                  focus:border-indigo-300
                  focus:ring-2
                  focus:ring-indigo-500/10
                  dark:border-gray-800
                  dark:bg-gray-900
                  dark:text-gray-200
                  dark:focus:border-indigo-700
                "
              />
            </div>

            {/* Course filters */}

            <div
              className="
                mt-3
                flex
                gap-2
                overflow-x-auto
                pb-1
                scrollbar-thin
                scrollbar-track-transparent
                scrollbar-thumb-gray-300
                dark:scrollbar-thumb-gray-700
              "
            >
              {["All", ...courses].map((course) => {
                const active = activeCourse === course;

                return (
                  <button
                    key={course}
                    type="button"
                    onClick={() =>
                      setActiveCourse(course)
                    }
                    className={`
                      shrink-0
                      rounded-lg
                      px-3
                      py-2
                      text-[9px]
                      font-bold
                      transition-all
                      ${active
                        ? `
                            bg-gray-950
                            text-white
                            dark:bg-white
                            dark:text-gray-950
                          `
                        : `
                            bg-white
                            text-gray-500
                            hover:text-indigo-600
                            dark:bg-gray-900
                            dark:text-gray-400
                            dark:hover:text-indigo-400
                          `
                      }
                    `}
                  >
                    {course}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Grid */}

          <div className="mt-7">
            {filteredStudents.length > 0 ? (
              <PlacementGrid
                students={filteredStudents}
                featuredLayout={false}
              />
            ) : (
              <div
                className="
                  rounded-2xl
                  border
                  border-dashed
                  border-gray-200
                  px-6
                  py-14
                  text-center
                  dark:border-gray-800
                "
              >
                <p
                  className="
                    text-sm
                    font-bold
                    text-gray-700
                    dark:text-gray-200
                  "
                >
                  No placement records found.
                </p>

                <p
                  className="
                    mt-1
                    text-xs
                    text-gray-400
                  "
                >
                  Try another search or course.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setActiveCourse("All");
                  }}
                  className="
                    mt-4
                    text-[10px]
                    font-bold
                    text-indigo-600
                    hover:text-indigo-700
                    dark:text-indigo-400
                  "
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>
      </section>


      {/* =====================================================
          STUDENT VIDEO EXPERIENCES
      ====================================================== */}

      <StudentVideoTestimonialsSection />

      {/* =====================================================
          STUDENT TESTIMONIALS
      ====================================================== */}

      <StudentTestimonialsSection />


      {/* =====================================================
          EXISTING COMPANY LOGO GRID
      ====================================================== */}

      <section
        aria-labelledby="companies-title"
        className="
          relative
          bg-[#fafbff]
          px-4
          py-12
          sm:px-6
          sm:py-14
          lg:px-8
          dark:bg-gray-950
        "
      >
        <div className="mx-auto max-w-7xl">

          <div className="mx-auto max-w-2xl text-center">
            <p
              className="
                text-[9px]
                font-bold
                uppercase
                tracking-[0.15em]
                text-indigo-600
                dark:text-indigo-400
              "
            >
              Hiring Destinations
            </p>

            <h2
              id="companies-title"
              className="
                mt-1.5
                text-2xl
                font-extrabold
                tracking-[-0.04em]
                text-gray-950
                sm:text-3xl
                dark:text-white
              "
            >
              Companies represented across our network
            </h2>

            <p
              className="
                mt-2
                text-xs
                leading-6
                text-gray-600
                sm:text-sm
                dark:text-gray-400
              "
            >
              Explore organizations represented across our
              placement and training ecosystem.
            </p>
          </div>

          <div className="mt-7">
            <PlacementLogoGrid />
          </div>
        </div>
      </section>



      {/* =====================================================
          FINAL CTA
      ====================================================== */}

      <section className="px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
        <motion.div
          initial={{
            opacity: 0,
            y: 12,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.45,
          }}
          className="
            relative
            mx-auto
            max-w-5xl
            overflow-hidden
            rounded-[24px]
            bg-gray-950
            px-6
            py-9
            text-center
            shadow-[0_18px_50px_rgba(15,23,42,0.12)]
            sm:px-10
            sm:py-11
            dark:bg-black
          "
        >
          <div
            aria-hidden="true"
            className="
              pointer-events-none
              absolute
              left-1/2
              top-[-70px]
              h-[190px]
              w-[320px]
              -translate-x-1/2
              rounded-full
              bg-indigo-500/20
              blur-[70px]
            "
          />

          <div className="relative z-10">
            <p
              className="
                text-[9px]
                font-bold
                uppercase
                tracking-[0.15em]
                text-cyan-300
              "
            >
              Start your journey
            </p>

            <h2
              className="
                mx-auto
                mt-2
                max-w-2xl
                text-2xl
                font-extrabold
                leading-tight
                tracking-[-0.04em]
                text-white
                sm:text-3xl
              "
            >
              Build skills that create
              <span className="text-indigo-300">
                {" "}career opportunities.
              </span>
            </h2>

            <p
              className="
                mx-auto
                mt-2.5
                max-w-xl
                text-xs
                leading-6
                text-white/60
                sm:text-sm
              "
            >
              Learn practical skills, build projects,
              prepare for interviews and take the next
              step toward your career.
            </p>

            <div className="mt-5">
              <Link
                to="/register"
                className="
                  group
                  inline-flex
                  items-center
                  gap-2
                  rounded-xl
                  bg-white
                  px-5
                  py-3
                  text-[11px]
                  font-bold
                  text-gray-950
                  transition-all
                  duration-200
                  hover:-translate-y-0.5
                  hover:bg-indigo-50
                "
              >
                Start your journey

                <HiArrowRight
                  className="
                    transition-transform
                    duration-200
                    group-hover:translate-x-0.5
                  "
                />
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </main>
  );
};

export default Placements;